<template>
  <div class="h-[60px] flex justify-between items-center px-4 sm:px-8 bg-surface-0 dark:bg-surface-950 relative lg:static border-b border-surface max-w-[100vw] overflow-hidden box-border">
    <div class="flex items-center">
      <a
        v-styleclass="{
          selector: '#app-sidebar-9',
          enterFromClass: 'hidden',
          enterActiveClass: 'animate-fadeinleft',
          leaveToClass: 'hidden',
          leaveActiveClass: 'animate-fadeoutleft',
          hideOnOutsideClick: true,
        }"
        class="cursor-pointer flex items-center gap-2 lg:hidden text-surface-700 dark:text-surface-100 mr-4 py-2 px-3 -ml-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
      >
        <i class="pi pi-bars text-2xl" />
        <span class="text-sm font-medium">Menu</span>
      </a>

      <!-- Staging indicator for desktop -->
      <div
        v-if="isStaging"
        class="hidden lg:flex items-center px-3 py-1 text-sm font-medium text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-full border border-red-200 dark:border-red-800"
      >
        <i class="pi pi-exclamation-triangle mr-2" />
        Staging Environment
      </div>
    </div>

    <!-- Mobile payment buttons and staging indicator -->
    <div class="flex items-center gap-2 lg:hidden">
      <!-- Mobile staging indicator -->
      <div
        v-if="isStaging"
        class="flex items-center px-2 py-1 text-xs font-medium text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-full border border-red-200 dark:border-red-800"
      >
        <i class="pi pi-exclamation-triangle mr-1 text-xs" />
        <span class="hidden sm:inline">Staging</span>
        <span class="sm:hidden">STAG</span>
      </div>

      <SharedPurchaseButton
        v-if="shouldPayDues"
        ticket-type="membership"
        icon="pi pi-dollar"
        button-label="Pay"
        button-class="p-button-rounded p-button-outlined p-button-success text-xs !px-2"
        button-size="small"
        no-min-width
      />
      <SharedPurchaseButton
        v-if="!shouldPayDues && canUpgradeToVip"
        ticket-type="vip"
        icon="pi pi-star"
        button-severity="info"
        button-label="Buy VIP"
        button-class="p-button-rounded p-button-outlined text-xs !px-2"
        button-size="small"
        no-min-width
      />
      <SharedPurchaseButton
        v-if="hasChildrenWithoutTickets"
        ticket-type="children"
        icon="pi pi-ticket"
        button-severity="success"
        button-label="Kid"
        button-class="p-button-rounded p-button-outlined text-xs !px-2"
        button-size="small"
        :children-ids="childrenWithoutTickets"
        no-min-width
        @order-complete="handleChildrenTicketsPurchased"
      />

      <!-- Mobile Redeem Code launcher -->
      <SharedRedeemCodeLauncher
        v-if="shouldPayDues"
        button-size="small"
        class="p-button-rounded p-button-outlined text-xs !px-2"
      />

      <!-- Mobile Chatbot button -->
      <button
        class="p-2 rounded-lg text-primary-500 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
        aria-label="Open chat assistant"
        @click="toggleChatbot"
      >
        <i class="pi pi-comments text-xl" />
      </button>
    </div>

    <!-- Desktop toolbar - hidden on mobile -->
    <ul
      class="list-none p-0 m-0 hidden lg:flex lg:items-center select-none lg:flex-row lg:ml-auto border lg:border-0 border-surface right-0 top-full z-40 shadow lg:shadow-none absolute lg:static gap-2"
    >
      <li
        v-if="shouldPayDues"
        class="relative"
      >
        <SharedPurchaseButton
          ticket-type="membership"
        />
      </li>
      <li
        v-if="!shouldPayDues && canUpgradeToVip"
        class="relative"
      >
        <SharedPurchaseButton
          ticket-type="vip"
          button-severity="info"
        />
      </li>
      <!-- Add children tickets purchase button -->
      <li
        v-if="hasChildrenWithoutTickets"
        class="relative"
      >
        <SharedPurchaseButton
          ticket-type="children"
          button-severity="success"
          button-label="Buy Children Tickets"
          icon="pi pi-ticket"
          :children-ids="childrenWithoutTickets"
          @order-complete="handleChildrenTicketsPurchased"
        />
      </li>
      <li
        v-if="shouldPayDues"
        class="relative"
      >
        <!-- Desktop Redeem Code launcher -->
        <SharedRedeemCodeLauncher />
      </li>
      <li class="relative">
        <!-- Notification Center -->
        <NotificationCenter
          class="flex p-4 lg:px-4 lg:py-2 items-center"
          prime-style
        />
      </li>
      <li class="border-t border-surface lg:border-t-0 relative">
        <!-- Desktop (Popover) -->
        <a
          ref="userMenuButton"
          class="hidden lg:flex p-4 lg:px-4 lg:py-2 items-center hover:bg-surface-100 dark:hover:bg-surface-700 font-medium rounded-border cursor-pointer duration-150 transition-colors"
          @click="toggleUserMenu"
        >
          <i
            class="pi pi-user text-base lg:!text-2xl leading-none mr-2 lg:mr-0"
            :class="{ 'text-red-500': isAdmin }"
          />
        </a>

        <!-- User Menu Popover -->
        <Popover
          ref="userPopover"
          :show-close-icon="false"
          position="bottom"
          class="user-popover"
          @show="onUserPopoverShow"
        >
          <div class="p-0 min-w-[200px]">
            <!-- User name header -->
            <div class="p-4 text-surface-900 dark:text-surface-0 font-medium border-b border-surface">
              {{ displayName }}
            </div>

            <!-- Menu items -->
            <a
              class="flex items-center cursor-pointer p-4 hover:bg-surface-100 dark:hover:bg-surface-700 hover:text-surface-900 rounded-border text-surface-600 dark:text-surface-200 dark:hover:text-surface-100 duration-150 transition-colors"
              @click="logout"
            >
              <i class="pi pi-sign-out mr-2" />
              <span class="font-medium">Sign Out</span>
            </a>
          </div>
        </Popover>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useCurrentUser } from 'vuefire'
import { NotificationCenter } from '~/components/notifications'
import { useChildrenTickets } from '~/composables/useChildrenTickets'
import { DuesStatusType, getMemberDuesStatus, useShouldPayDues } from '~/composables/useShouldPayDues.js'
import { MemberType, isRedshirt } from '~/shared/types/memberType'

defineProps({
  displayName: {
    type: String,
    default: '',
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
})

const userMenuButton = ref(null)
const userPopover = ref()
const membersStore = useMembersStore()
const currentUser = useCurrentUser()
const paradeYear = useRuntimeConfig().public.nextParadeYear
const runtimeConfig = useRuntimeConfig()
const toast = useToast()

// Environment detection
const isStaging = computed(() => runtimeConfig.public.krewebacchusEnv === 'staging')

const shouldPayDues = useShouldPayDues()

// Chatbot toggle for mobile
const { toggleChat: toggleChatbot } = useChatbot()

// Use the children tickets composable
const { childrenWithoutTickets, hasChildrenWithoutTickets } = useChildrenTickets()

const hasVip = computed(() => {
  if (!currentUser.value?.uid) return false

  const memberPath = `members/${currentUser.value.uid}`
  const member = membersStore.members[memberPath]

  // Use the new array-based purchase structure
  const yearlyPurchases = member?.purchases?.[paradeYear]
  if (yearlyPurchases?.vip && Array.isArray(yearlyPurchases.vip) && yearlyPurchases.vip.length > 0) {
    return yearlyPurchases.vip.some(purchase => purchase.status === 'completed')
  }

  return false
})

const canUpgradeToVip = computed(() => {
  if (hasVip.value) return false // Can't upgrade if already VIP

  const memberPath = `members/${currentUser.value?.uid}`
  const member = membersStore.members[memberPath]

  if (!member) return false

  // Redshirts can upgrade to VIP (they don't pay dues, so skip dues check for them)
  if (isRedshirt(member) || member.member_type === MemberType.REDSHIRT) {
    return true
  }

  // Non-redshirts must have paid dues first
  if (shouldPayDues.value) return false

  // Get the dues status to check the specific reason why the member doesn't need to pay
  const { statusType } = getMemberDuesStatus(member)

  // If the exemption is due to pending/denied SubKrewe approval, don't show VIP upgrade
  if (statusType === DuesStatusType.EXEMPT_PENDING_APPROVAL) {
    return false
  }

  // In all other cases where dues are paid or exempt (lifetime, redshirt, band captain),
  // allow VIP upgrade
  return true
})

// Handle successful children tickets purchase
const handleChildrenTicketsPurchased = (_event) => {
  toast.add({
    severity: 'success',
    summary: 'Tickets Purchased',
    detail: `Tickets for your children have been purchased`,
    life: 3000,
  })
}

// Toggle user menu popover
const toggleUserMenu = (event) => {
  if (userPopover.value) {
    userPopover.value.toggle(event)
  }
}

// Call alignOverlay after popover is shown
const onUserPopoverShow = () => {
  // Allow DOM to update before realigning
  setTimeout(() => {
    if (userPopover.value) {
      userPopover.value.alignOverlay()
    }
  }, 0)
}

const logout = async () => {
  // Hide popover if it's open
  if (userPopover.value) {
    userPopover.value.hide()
  }

  const { signOutUser } = useAuth()

  await signOutUser()
}
</script>

<style scoped>
/* Override Popover styling for user menu */
:deep(.user-popover.p-popover) {
  max-width: 95vw;
}

/* Fix arrow position for user popover */
:deep(.user-popover.p-popover.p-popover-bottom .p-popover-arrow) {
  left: calc(var(--overlayArrowLeft, 0) + 12px) !important;
}

/* On mobile, make popover wider */
@media (max-width: 640px) {
  :deep(.user-popover .p-popover-content) {
    min-width: 90vw !important;
  }
}
</style>
